import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useEffect, useState } from 'react';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';
import { AzureResourceGroup, useAzureResourceGroups } from './useAzureResourceGroups';

const DEFAULT_SUBSCRIPTION_FIELD = 'subscription';

export const AzureResourceGroupPickerFieldSchema = makeFieldSchema({
  output: z =>
    z.object({
      id: z.string().describe('The ARM resource id of the selected resource group'),
      name: z.string().describe('The name of the selected resource group'),
      location: z.string().describe('The Azure location of the selected resource group'),
    }),
  uiOptions: z =>
    z.object({
      subscriptionField: z
        .string()
        .optional()
        .describe(
          'Name of the sibling parameter holding the AzureSubscriptionPicker value (defaults to "subscription")',
        ),
    }),
});

type AzureResourceGroupPickerProps = typeof AzureResourceGroupPickerFieldSchema.TProps;

const getResourceGroupFromForm = (form: any): AzureResourceGroup | null =>
  form?.id && form?.name && form?.location ? (form as AzureResourceGroup) : null;

// Resource group ARM ids look like /subscriptions/{subscriptionId}/resourceGroups/{name},
// so this lets us tell whether a previously selected resource group still belongs to the
// currently selected subscription without an extra network call.
const belongsToSubscription = (
  resourceGroup: AzureResourceGroup | null,
  subscriptionId: string | undefined,
): boolean =>
  Boolean(
    resourceGroup &&
      subscriptionId &&
      resourceGroup.id.toLowerCase().startsWith(`/subscriptions/${subscriptionId.toLowerCase()}/`),
  );

const AzureResourceGroupPicker = (props: AzureResourceGroupPickerProps) => {
  const {
    schema: { title = 'Azure resource group', description = '' },
    uiSchema,
    onChange,
    rawErrors,
    required,
    formData,
    formContext,
    errors,
  } = props;

  const subscriptionField =
    uiSchema?.['ui:options']?.subscriptionField ?? DEFAULT_SUBSCRIPTION_FIELD;
  const subscriptionId: string | undefined = (formContext as any)?.formData?.[subscriptionField]
    ?.subscriptionId;

  const { resourceGroups, isLoading, error, hasToken } = useAzureResourceGroups(subscriptionId);
  const [selected, setSelected] = useState<AzureResourceGroup | null>(
    getResourceGroupFromForm(formData),
  );

  useEffect(() => {
    setSelected(getResourceGroupFromForm(formData));
  }, [formData]);

  // Clear the selection if the subscription changes to one the current selection
  // doesn't belong to, so a stale resource group can never be submitted.
  useEffect(() => {
    if (selected && !belongsToSubscription(selected, subscriptionId)) {
      setSelected(null);
    }
  }, [subscriptionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const isValid = selected?.id && selected?.name && selected?.location;
    onChange(isValid ? selected : undefined);
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const getHelperText = () => {
    if (!hasToken) return 'Waiting for Azure sign-in…';
    if (!subscriptionId) return 'Select an Azure subscription first';
    if (error instanceof Error) return error.message;
    return 'Select a resource group from the list';
  };

  return (
    <ScaffolderField
      rawErrors={rawErrors}
      rawDescription={description}
      required={required}
      errors={errors}
    >
      <Autocomplete
        options={resourceGroups ?? []}
        value={selected}
        getOptionLabel={resourceGroup => resourceGroup?.name ?? ''}
        getOptionSelected={(option, value) => option.id === value.id}
        onChange={(_, newValue) => setSelected(newValue)}
        loading={isLoading}
        disabled={!hasToken || !subscriptionId}
        renderInput={params => (
          <TextField
            {...params}
            required={required}
            label={title}
            error={Boolean(error)}
            helperText={getHelperText()}
          />
        )}
      />
    </ScaffolderField>
  );
};

export default AzureResourceGroupPicker;
